/**
 * Crunch Design System — Modal, ModalHeader, ModalFooter, AlertModal
 *
 * Figma components:
 *   modal-header (node 551:3858)
 *   modal-footer (node 551:3851)
 *   modal        (node 3395:1361, key generic)
 *   alert-modal  (node 3085:1364)
 *
 * Usage:
 *   <Modal title="Edit item" onClose={close} footer={<ModalFooter>…</ModalFooter>}>
 *     <p>Body content here.</p>
 *   </Modal>
 *
 *   <AlertModal title="Delete item?" onClose={close}
 *     actionLabel="Delete" actionType="danger" onAction={del} onCancel={close}>
 *     <p>This action cannot be undone.</p>
 *   </AlertModal>
 */
import * as React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faXmark } from '@awesome.me/kit-8a0d2fd588/icons/modules/classic/light';

import { Button, IconButton } from "./Button";

export function ModalHeader({
    title = "Modal title",
    hasCloseButton = true,
    onClose,
    className,
    ref,
    ...rest
}: {
    title?: string;
    hasCloseButton?: boolean;
    onClose?: () => void;
    className?: string;
    ref?: React.Ref<HTMLDivElement>;
    [key: string]: any;
}) {
    return (
        <div className={["cr-modal-header", className].filter(Boolean).join(" ")} ref={ref} {...rest}>
            <h2 className="cr-modal-header__title">{title}</h2>
            {hasCloseButton && (
                <IconButton
                    type="floating"
                    icon={<FontAwesomeIcon icon={faXmark} />}
                    label="Close"
                    onClick={onClose}
                />
            )}
        </div>
    );
}

export function ModalFooter({
    tertiaryAction,
    children,
    className,
    ref,
    ...rest
}: {
    tertiaryAction?: React.ReactNode;
    children?: React.ReactNode;
    className?: string;
    ref?: React.Ref<HTMLDivElement>;
    [key: string]: any;
}) {
    return (
        <div className={["cr-modal-footer", className].filter(Boolean).join(" ")} ref={ref} {...rest}>
            {tertiaryAction || null}
            <div className="cr-modal-footer__button-group">
                {children}
            </div>
        </div>
    );
}

export function Modal({
    title = "Modal title",
    hasCloseButton = true,
    onClose,
    footer,
    children,
    className,
    ref,
    ...rest
}: {
    title?: string;
    hasCloseButton?: boolean;
    onClose?: () => void;
    footer?: React.ReactNode;
    children?: React.ReactNode;
    className?: string;
    ref?: React.Ref<HTMLDivElement>;
    [key: string]: any;
}) {
    return (
        <div className={["cr-modal", className].filter(Boolean).join(" ")} ref={ref} {...rest}>
            <ModalHeader title={title} hasCloseButton={hasCloseButton} onClose={onClose} />
            <div className="cr-modal-body">
                {children}
            </div>
            {footer}
        </div>
    );
}

export function AlertModal({
    title = "Alert title",
    hasCloseButton = true,
    onClose,
    children,
    cancelLabel = "Cancel",
    onCancel,
    actionLabel = "Confirm",
    actionType = "danger",
    onAction,
    className,
    ref,
    ...rest
}: {
    title?: string;
    hasCloseButton?: boolean;
    onClose?: () => void;
    children?: React.ReactNode;
    cancelLabel?: string;
    onCancel?: () => void;
    actionLabel?: string;
    actionType?: "primary" | "danger" | "warning" | "important";
    onAction?: () => void;
    className?: string;
    ref?: React.Ref<HTMLDivElement>;
    [key: string]: any;
}) {
    return (
        <div className={["cr-modal cr-modal--alert", className].filter(Boolean).join(" ")} ref={ref} {...rest}>
            <ModalHeader title={title} hasCloseButton={hasCloseButton} onClose={onClose} />
            <div className="cr-modal-body">
                {children}
            </div>
            <ModalFooter>
                <Button type="secondary" onClick={onCancel}>{cancelLabel}</Button>
                <Button type={actionType} onClick={onAction}>{actionLabel}</Button>
            </ModalFooter>
        </div>
    );
}
